import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    Input,
    OnInit,
    Output,
    ViewChild,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { NgScrollbar } from 'ngx-scrollbar';
import { PickSourceProvider } from 'packages/sports/web/app/src/option-pick/pick-source.provider';
import { Subscription, take } from 'rxjs';

import { IRewardToken, IRewardTokenInfo, RewardBannerTrackingConstants } from '../../reward-tokens.model';
import { AcquisitionRewardTokenService } from '../../services/acquisition-reward-token.service';
import { RewardTokensBannerService } from '../../services/reward-tokens-banner.service';
import { getAcquisitionRewardToken } from '../../services/reward-tokens.utils';

@Component({
    selector: 'bs-reward-tokens-list',
    templateUrl: 'reward-tokens-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./reward-tokens-list.component.scss'],
    standalone: false,
})
export class RewardTokensListComponent implements OnInit, AfterViewInit {
    @Input() tokenInfos: IRewardTokenInfo[];
    @Output() rewardBannerDismiss = new EventEmitter<Event>();
    @Input() isRewardDrawerEnabled: boolean;

    @ViewChild(NgScrollbar) scrollbar: NgScrollbar;

    private showAcquisitionRewardNotification = false;
    private readonly destroyRef = inject(DestroyRef);
    private scrollBarSubscription: Subscription;

    @HostBinding('class.reward-drawer__list') get rewardDrawerEnabled(): boolean {
        return this.isRewardDrawerEnabled;
    }

    messagesAndConfigs: {
        promoHubUrl?: string;
        promotionalTitle: string;
        promoHubLinkText: string;
        dismissLinkText: string;
        showDismissLink: boolean;
    };

    acquisitionRewardToken: IRewardToken;
    isAcquisitionNotificationVisible = signal(false);

    constructor(
        private sitecore: Sitecore,
        private tokensConfig: CrmConfig,
        private rewardTokensBannerService: RewardTokensBannerService,
        private pickSourceProvider: PickSourceProvider,
        private acquisitionRewardTokenService: AcquisitionRewardTokenService,
    ) {}

    ngOnInit(): void {
        this.messagesAndConfigs = {
            promoHubUrl: this.sitecore.crmTokens.links.promotions.url,
            promotionalTitle: this.sitecore.crmTokens.banner.RewardBannerPromotionalTitle,
            promoHubLinkText: this.sitecore.crmTokens.banner.RewardBannerViewFullDetailsTitle,
            dismissLinkText: this.sitecore.crmTokens.banner.RewardBannerDismissOrRemindMeLaterTitle,
            showDismissLink: this.tokensConfig.tokens.isDismissOrRemindMeLaterEnabled,
        };
        this.showAcquisitionNotification();
    }

    ngAfterViewInit(): void {
        if (!this.showAcquisitionRewardNotification || !this.scrollbar.scrolled) {
            return;
        }

        this.scrollBarSubscription = this.scrollbar.scrolled
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.hideAcquisitionNotification());
    }

    promoHubLinkClicked(): void {
        const promoHubLinkText = this.isRewardDrawerEnabled ? 'Go to Rewards Hub' : this.messagesAndConfigs.promoHubLinkText;
        this.trackBanner(promoHubLinkText, this.messagesAndConfigs.promoHubUrl);
    }

    dismissRewardBanner(event: Event): void {
        const dismissLinkText = this.isRewardDrawerEnabled ? 'Remind me later' : this.messagesAndConfigs.dismissLinkText;
        this.trackBanner(dismissLinkText);
        this.rewardBannerDismiss.emit(event);
    }

    hideAcquisitionNotification(): void {
        this.scrollBarSubscription?.unsubscribe();
        this.isAcquisitionNotificationVisible.set(false);
    }

    private showAcquisitionNotification(): void {
        const tokens = this.tokenInfos.map((t) => t.token);
        this.showAcquisitionRewardNotification = this.acquisitionRewardTokenService.shouldShowAcquisitionRewardNotification(tokens);

        if (this.showAcquisitionRewardNotification) {
            this.acquisitionRewardToken = getAcquisitionRewardToken(tokens)!;
            this.isAcquisitionNotificationVisible.set(true);
        }
    }

    private trackBanner(eventDetail: string, url?: string): void {
        this.rewardTokensBannerService.trackBetRewardBanner(
            RewardBannerTrackingConstants.Click,
            this.pickSourceProvider.getTracking(),
            eventDetail,
            RewardBannerTrackingConstants.RewardsBanner,
            url,
        );
    }
}
